import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, List, ListItem, ListItemText, ListItemSecondaryAction,
    IconButton, Typography, Box, TextField, Chip, Divider,
    CircularProgress
} from '@mui/material';
import {
    History as HistoryIcon,
    CloudUpload as UploadIcon,
    Restore as RestoreIcon,
    GetApp as DownloadIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';
import { devLog } from '../../utils/devLog';

interface MediaVersion {
    id: number;
    versionNumber: number;
    filename: string;
    fileSize: string | number;
    mimeType: string;
    uploadComment?: string;
    createdAt: string | Date;
    uploadedBy: number;
}

interface FileVersionDialogProps {
    open: boolean;
    file: any | null;
    onClose: () => void;
    onUpdate: () => void;
}

const FileVersionDialog: React.FC<FileVersionDialogProps> = ({ open, file, onClose, onUpdate }) => {
    const [versions, setVersions] = useState<MediaVersion[]>([]);
    const [currentVersionId, setCurrentVersionId] = useState<number | undefined>();
    const [loading, setLoading] = useState(false);
    const [uploadingVersion, setUploadingVersion] = useState(false);
    const [uploadComment, setUploadComment] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const dataProvider = useDataProvider();
    const notify = useNotify();

    useEffect(() => {
        if (open && file) {
            loadVersions();
        }
    }, [open, file]);

    const loadVersions = async () => {
        if (!file) return;

        setLoading(true);
        try {
            // Datei neu laden, um aktuelle Versionen zu erhalten
            const { data } = await dataProvider.getOne('media_files', { id: file.id });

            if (data.versions && Array.isArray(data.versions)) {
                devLog('Loaded versions:', data.versions);

                // Versionen sind als IRI-Strings oder IDs zurückgegeben, wir müssen sie laden
                const versionPromises = data.versions.map(async (versionRef: any) => {
                    let versionId: number;

                    if (typeof versionRef === 'object' && versionRef.id) {
                        // Bereits ein vollständiges Objekt
                        return versionRef;
                    } else if (typeof versionRef === 'string') {
                        // IRI wie "/api/media_versions/11" oder nur "11"
                        const match = versionRef.match(/\/(\d+)$/);
                        versionId = match ? parseInt(match[1], 10) : parseInt(versionRef, 10);
                    } else if (typeof versionRef === 'number') {
                        versionId = versionRef;
                    } else {
                        return null;
                    }

                    // Version-Objekt von der API laden
                    try {
                        const { data: versionData } = await dataProvider.getOne('media_versions', { id: versionId });
                        return versionData;
                    } catch (error) {
                        console.error('Failed to load version:', versionId, error);
                        return null;
                    }
                });

                const loadedVersions = await Promise.all(versionPromises);
                const validVersions = loadedVersions.filter(v => v !== null) as MediaVersion[];

                // Versionen nach Versionsnummer sortieren (neueste zuerst)
                const sortedVersions = validVersions.sort((a, b) => b.versionNumber - a.versionNumber);
                devLog('Sorted versions:', sortedVersions);
                setVersions(sortedVersions);
            }

            // Aktuelle Version ID setzen
            devLog('Current version from API:', data.currentVersion);
            if (data.currentVersion) {
                if (typeof data.currentVersion === 'object' && data.currentVersion.id) {
                    devLog('Setting current version ID (object):', data.currentVersion.id);
                    setCurrentVersionId(data.currentVersion.id);
                } else if (typeof data.currentVersion === 'string') {
                    // Could be IRI like "/api/media_versions/11" or just "11"
                    const match = data.currentVersion.match(/\/(\d+)$/);
                    const id = match ? parseInt(match[1], 10) : parseInt(data.currentVersion, 10);
                    devLog('Setting current version ID (string):', id);
                    setCurrentVersionId(id);
                } else if (typeof data.currentVersion === 'number') {
                    devLog('Setting current version ID (number):', data.currentVersion);
                    setCurrentVersionId(data.currentVersion);
                }
            } else {
                devLog('No current version found');
                setCurrentVersionId(undefined);
            }
        } catch (error) {
            notify('Fehler beim Laden der Versionen', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            setSelectedFile(files[0]);
        }
    };

    const handleUploadVersion = async () => {
        if (!selectedFile || !file) return;

        setUploadingVersion(true);
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('uploadComment', uploadComment);

            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/media/upload-version/${file.id}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData
            });

            const result = await response.json();

            if (result.success) {
                notify('Neue Version erfolgreich hochgeladen', { type: 'success' });
                setSelectedFile(null);
                setUploadComment('');
                onUpdate();
                loadVersions();
            } else {
                notify(result.error || 'Fehler beim Hochladen', { type: 'error' });
            }
        } catch (error) {
            notify('Fehler beim Hochladen der neuen Version', { type: 'error' });
        } finally {
            setUploadingVersion(false);
        }
    };

    const handleRollback = async (versionId: number) => {
        if (!file || !window.confirm('Möchten Sie wirklich zu dieser Version zurückkehren?')) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/media/rollback-version/${file.id}/${versionId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const result = await response.json();

            if (result.success) {
                notify('Version erfolgreich wiederhergestellt', { type: 'success' });
                onUpdate();
                loadVersions();
            } else {
                notify(result.error || 'Fehler beim Wiederherstellen', { type: 'error' });
            }
        } catch (error) {
            notify('Fehler beim Wiederherstellen der Version', { type: 'error' });
        }
    };

    const formatFileSize = (bytes: string | number | undefined): string => {
        if (!bytes) return '0 B';
        const size = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
        if (isNaN(size)) return '0 B';
        if (size < 1024) return size + ' B';
        if (size < 1024 * 1024) return (size / 1024).toFixed(2) + ' KB';
        return (size / (1024 * 1024)).toFixed(2) + ' MB';
    };

    const formatDate = (dateString: string | Date | undefined): string => {
        if (!dateString) return 'Unbekannt';
        const date = dateString instanceof Date ? dateString : new Date(dateString);
        if (isNaN(date.getTime())) return 'Unbekannt';
        return date.toLocaleString('de-DE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };


    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon />
                    Versionsverlauf
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {file?.originalFilename}
                </Typography>
            </DialogTitle>
            <DialogContent>
                {/* Upload neue Version */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Neue Version hochladen
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadIcon />}
                            fullWidth
                        >
                            {selectedFile ? selectedFile.name : 'Datei auswählen'}
                            <input
                                type="file"
                                hidden
                                onChange={handleFileSelect}
                            />
                        </Button>
                        <TextField
                            label="Kommentar zur neuen Version (optional)"
                            value={uploadComment}
                            onChange={(e) => setUploadComment(e.target.value)}
                            multiline
                            rows={2}
                            fullWidth
                            size="small"
                        />
                        <Button
                            variant="contained"
                            onClick={handleUploadVersion}
                            disabled={!selectedFile || uploadingVersion}
                            startIcon={uploadingVersion ? <CircularProgress size={20} /> : <UploadIcon />}
                        >
                            {uploadingVersion ? 'Wird hochgeladen...' : 'Version hochladen'}
                        </Button>
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Versionsliste */}
                <Typography variant="subtitle2" gutterBottom>
                    Vorhandene Versionen
                </Typography>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <List>
                        {versions.map((version) => {
                            const isCurrentVersion = version.id === currentVersionId;
                            return (
                                <ListItem
                                    key={version.id}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: isCurrentVersion ? 'primary.main' : 'divider',
                                        borderRadius: 1,
                                        mb: 1,
                                        bgcolor: isCurrentVersion ? 'action.selected' : 'background.paper',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'flex-start',
                                        pr: !isCurrentVersion ? 8 : 2
                                    }}
                                >
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <Typography variant="subtitle2">
                                                Version {version.versionNumber}
                                            </Typography>
                                            {isCurrentVersion && (
                                                <Chip
                                                    label="Aktuelle Version"
                                                    size="small"
                                                    color="primary"
                                                    icon={<CheckCircleIcon />}
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(version.createdAt)} · {formatFileSize(version.fileSize)}
                                        </Typography>
                                        {version.uploadComment && (
                                            <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                                "{version.uploadComment}"
                                            </Typography>
                                        )}
                                    </Box>
                                    {!isCurrentVersion && (
                                        <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}>
                                            <IconButton
                                                onClick={() => handleRollback(version.id)}
                                                title="Zu dieser Version zurückkehren"
                                            >
                                                <RestoreIcon />
                                            </IconButton>
                                        </Box>
                                    )}
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Schließen</Button>
            </DialogActions>
        </Dialog>
    );
};

export default FileVersionDialog;
